import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { setHeaderText } from "../../../features/headerText/headerTextSlice";
import SettingsIcon from "../../../images/icons/settings.svg";
import "./MvpClientChrome.css";

function clientPhoto(client) {
  const logo = client?.company_logo;
  if (!logo || logo === "null") return "";
  return `/uploads/clientCompyLogo/lowQuality/${logo}`;
}

function ClientIdentity({ client, active, showPhoto }) {
  const photo = showPhoto ? clientPhoto(client) : "";
  return (
    <>
      <span className={`mvp-cc-gear${photo ? " is-photo" : ""}`}>
        <img src={photo || SettingsIcon} alt="" />
      </span>
      <h1>{client?.client_name || client?.legal_entity_name || "Client"}</h1>
      <span className={`mvp-cc-status ${active ? "" : "is-off"}`}>
        {active ? "Active" : "Inactive"}
      </span>
    </>
  );
}

function tabSection(pathname) {
  if (pathname.includes("/client/profile/")) return "profile";
  if (pathname.includes("/client/projects/")) return "projects";
  return "details";
}

const SECTION_OPTIONS = [
  { id: "details", label: "Overview" },
  { id: "projects", label: "Projects" },
  { id: "profile", label: "Client Profile" },
];

function MobileSectionSelect({ encryptedId, section }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const encoded = encodeURIComponent(encryptedId || "");
  const value = section === "projects" ? "projects" : section === "profile" ? "profile" : "details";
  const current = SECTION_OPTIONS.find((option) => option.id === value);
  return (
    <div className="mvp-cc-mobile-nav" onBlur={() => setOpen(false)}>
      <button type="button" className="mvp-cc-mobile-trigger" onClick={() => setOpen((currentOpen) => !currentOpen)}>
        <span>{current?.label || "Overview"}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="11" viewBox="0 0 19 11" fill="none">
          <path d="M0.423828 0.423828L9.42383 9.42383L18.4238 0.423828" stroke-width="1.2" stroke="#405BFF" />
        </svg>
      </button>
      {open && (
        <ul className="mvp-cc-mobile-menu">
          {SECTION_OPTIONS.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className={option.id === value ? "is-active" : ""}
                onMouseDown={(event) => {
                  event.preventDefault();
                  setOpen(false);
                  if (option.id !== value) navigate(`/client/${option.id}/${encoded}`);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function MvpClientChrome({ client, encryptedId, children }) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const section = tabSection(pathname);
  const encoded = encodeURIComponent(encryptedId || "");
  const active = client?.is_active !== false;

  useEffect(() => {
    dispatch(setHeaderText("Clients"));
  }, [dispatch]);

  const identity = (
    <div className="mvp-cc-identity">
      <ClientIdentity client={client} active={active} showPhoto={section === "profile"} />
    </div>
  );

  return (
    <div className="mvp-cc">
      <div className="mvp-cc-identity mvp-cc-identity-flow">
        <ClientIdentity client={client} active={active} showPhoto={section === "profile"} />
      </div>
      {typeof document !== "undefined" ? createPortal(identity, document.body) : null}
      <section className="mvp-cc-card">
        <nav className="mvp-cc-tabs">
          <NavLink className={() => (section === "details" ? "active" : undefined)} to={`/client/details/${encoded}`}>Overview</NavLink>
          <NavLink className={() => (section === "projects" ? "active" : undefined)} to={`/client/projects/${encoded}`}>Projects</NavLink>
          <NavLink className={() => (section === "profile" ? "active" : undefined)} to={`/client/profile/${encoded}`}>Client Profile</NavLink>
        </nav>
        <div className="mvp-cc-panel">
          <MobileSectionSelect encryptedId={encryptedId} section={section} />
          {children}
        </div>
      </section>
    </div>
  );
}
